import React, { useState, useEffect, useCallback } from "react";
import "./work_report.css";
import { toast } from "react-toastify";
import {
  addReport,
  getAllAssignedProjects,
} from "../../service/project_service";
import { useLocation } from "react-router-dom";
import { CustomSearchableSelect } from "../../components/admin/AdminFields";

const AddWorkReport = () => {
  const location = useLocation();
  const { projectid, moduleid, projecttaskid, taskname } = location.state || {};

  const [formData, setFormData] = useState({
    projectid: projectid || "",
    moduleid: moduleid || "",
    projecttaskid: projecttaskid || "",
    workdate: new Date().toISOString().split("T")[0],
    starttime: "",
    endtime: "",
    workinghours: "",
    taskname: taskname || "",
  });

  const [projects, setProjects] = useState([]);
  const [modules, setModules] = useState([]);
  const [availableTasks, setAvailableTasks] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchProjects = useCallback(async () => {
    try {
      const response = await getAllAssignedProjects();
      if (response?.status === "success") {
        setProjects(response.data || []);
      }
    } catch (error) {
      toast.error(error?.message || "Failed to fetch projects");
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Update modules and tasks when project/module changes
  useEffect(() => {
    if (formData.projectid && projects.length > 0) {
      const selectedProject = projects.find(
        (p) => Number(p.projectid) === Number(formData.projectid),
      );
      setModules(selectedProject?.modules || []);

      if (formData.moduleid) {
        const selectedModule = selectedProject?.modules?.find(
          (m) => Number(m.moduleid) === Number(formData.moduleid),
        );
        setAvailableTasks(selectedModule?.tasks || []);
      } else {
        setAvailableTasks([]);
      }
    } else {
      setModules([]);
      setAvailableTasks([]);
    }
  }, [formData.projectid, projects, formData.moduleid]);

  // Calculate duration
  useEffect(() => {
    if (formData.starttime && formData.endtime) {
      const [startH, startM] = formData.starttime.split(":").map(Number);
      const [endH, endM] = formData.endtime.split(":").map(Number);

      const startTotalMins = startH * 60 + startM;
      const endTotalMins = endH * 60 + endM;

      if (endTotalMins > startTotalMins) {
        const diffMins = endTotalMins - startTotalMins;
        const h = Math.floor(diffMins / 60);
        const m = diffMins % 60;
        const formatted = `${h}.${m.toString().padStart(2, "0")}`;
        setFormData((prev) => ({
          ...prev,
          workinghours: formatted,
        }));
      } else {
        setFormData((prev) => ({ ...prev, workinghours: "" }));
      }
    }
  }, [formData.starttime, formData.endtime]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleReset = () => {
    setFormData({
      projectid: projectid || "",
      moduleid: moduleid || "",
      projecttaskid: "",
      workdate: new Date().toISOString().split("T")[0],
      starttime: "",
      endtime: "",
      workinghours: "",
      taskname: "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.projectid ||
      !formData.workdate ||
      !formData.taskname.trim()
    ) {
      toast.error("Please fill all required fields (Project, Date, and Description)");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        projectid: Number(formData.projectid),
        moduleid: formData.moduleid ? Number(formData.moduleid) : 0,
        workdate: formData.workdate,
        starttime: formData.starttime || "0.00",
        endtime: formData.endtime || "0.00",
        workinghours: formData.workinghours
          ? Number(formData.workinghours).toFixed(2)
          : "0.00",
        taskname: formData.taskname.trim(),
        projecttaskid: formData.projecttaskid ? Number(formData.projecttaskid) : 0,
      };

      const response = await addReport(payload);
      if (response?.status === "success") {
        toast.success(response?.message || "Report added successfully");
        handleReset();
      } else {
        toast.error(response?.message || "Failed to add report");
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-report-container">
      <h4>Add Work Report</h4>

      <div className="add-report-card">
        <form className="add-report-form" onSubmit={handleSubmit}>
          <div className="report-form-group">
            <CustomSearchableSelect
              label="Project *"
              placeholder="Select Project"
              value={formData.projectid}
              required={true}
              onChange={(e) =>
                handleInputChange({
                  target: { name: "projectid", value: e.target.value },
                })
              }
              options={projects.map((p) => ({
                label: p.projectname,
                value: p.projectid,
              }))}
            />
          </div>

          <div className="report-form-group">
            <CustomSearchableSelect
              label="Project Module"
              placeholder="Select Module"
              value={formData.moduleid}
              onChange={(e) =>
                handleInputChange({
                  target: { name: "moduleid", value: e.target.value },
                })
              }
              options={modules.map((m) => ({
                label: m.modulename,
                value: m.moduleid,
              }))}
            />
          </div>

          <div className="report-form-group">
            <CustomSearchableSelect
              label="Assigned Task "
              placeholder={availableTasks.length > 0 ? "Pick an assigned task" : "No linked tasks found"}
              value={formData.projecttaskid}
              onChange={(e) => {
                const selectedTask = availableTasks.find(t => String(t.projecttaskid) === String(e.target.value));
                setFormData(prev => ({
                  ...prev,
                  projecttaskid: e.target.value,
                  taskname: selectedTask ? (prev.taskname || selectedTask.taskname) : prev.taskname
                }));
              }}
              options={availableTasks
                .filter((t) => t.taskname && t.projecttaskid)
                .map((t) => ({
                  label: t.taskname,
                  value: t.projecttaskid,
                }))}
            />
          </div>

          <div className="report-form-group">
            <label>Work Date *</label>
            <input
              type="date"
              name="workdate"
              value={formData.workdate}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="report-form-group">
            <label>Start Time</label>
            <input
              type="time"
              name="starttime"
              value={formData.starttime}
              onChange={handleInputChange}
            />
          </div>

          <div className="report-form-group">
            <label>End Time</label>
            <input
              type="time"
              name="endtime"
              value={formData.endtime}
              onChange={handleInputChange}
            />
          </div>

          <div className="report-form-group">
            <label>Working Hours</label>
            <input
              type="text"
              name="workinghours"
              value={formData.workinghours}
              onChange={handleInputChange}
              readOnly
            />
          </div>

          <div className="report-form-group report-form-group-full">
            <label>Work Description / Remarks *</label>
            <textarea
              name="taskname"
              value={formData.taskname}
              onChange={handleInputChange}
              rows="4"
              placeholder="Describe the work done or select from tasks above..."
              required
            />
          </div>

          <div className="submit-buttons report-form-group-full">
            <button type="submit" className="apply-btn" disabled={loading}>
              {loading ? "Loading..." : "Submit"}
            </button>
            <button
              type="button"
              className="reset-btn"
              onClick={handleReset}
              disabled={loading}
            >
              Reset
            </button>
          </div>
        </form>
      </div>
      <div style={{ height: "50px" }}></div>
    </div>
  );
};

export default AddWorkReport;

